import { type ParsedUrlQuery } from 'querystring'
import { APP_ROUTES } from 'shared/routes'
import { buildReturnToQueryParam, isValidReturnToUrl, parseSafeReturnToUrl } from 'shared/utils'

const STORAGE_KEY = 'alvie:pendingInviteReturnTo'
// Match invite token max age in server/messages/service.py
const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000

interface StoredPendingInviteReturnTo {
  returnTo: string
  expiresAt: number
}

interface SafeReturnTo {
  pathname: string
  queryParams: ParsedUrlQuery
}

export const isJoinWorkspaceReturnTo = (safeReturnTo: SafeReturnTo | null): safeReturnTo is SafeReturnTo =>
  safeReturnTo?.pathname === APP_ROUTES.workspace.joinWorkspacePage

export const persistPendingInviteReturnTo = (returnTo: string | undefined, baseUrl: string): void => {
  if (typeof window === 'undefined' || !isValidReturnToUrl(returnTo, baseUrl)) {
    return
  }

  const safeReturnTo = parseSafeReturnToUrl(returnTo, baseUrl)
  if (!isJoinWorkspaceReturnTo(safeReturnTo)) {
    return
  }

  const stored: StoredPendingInviteReturnTo = {
    returnTo: buildReturnToQueryParam(safeReturnTo),
    expiresAt: Date.now() + THIRTY_DAYS_MS
  }

  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(stored))
  } catch {
    // localStorage can throw in private browsing or when quota is exceeded.
  }
}

export const clearPendingInviteReturnTo = (): void => {
  if (typeof window === 'undefined') {
    return
  }

  try {
    localStorage.removeItem(STORAGE_KEY)
  } catch {
    // Ignore storage failures on clear.
  }
}

export const getPendingInviteReturnTo = (baseUrl: string): string | null => {
  if (typeof window === 'undefined') {
    return null
  }

  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (raw == null) {
      return null
    }

    const stored = JSON.parse(raw) as StoredPendingInviteReturnTo
    if (stored.expiresAt <= Date.now()) {
      clearPendingInviteReturnTo()
      return null
    }

    if (!isValidReturnToUrl(stored.returnTo, baseUrl)) {
      clearPendingInviteReturnTo()
      return null
    }

    const safeReturnTo = parseSafeReturnToUrl(stored.returnTo, baseUrl)
    if (!isJoinWorkspaceReturnTo(safeReturnTo)) {
      clearPendingInviteReturnTo()
      return null
    }

    return stored.returnTo
  } catch {
    clearPendingInviteReturnTo()
    return null
  }
}

export const getEffectiveReturnTo = (returnTo: string | undefined, baseUrl: string): string | undefined => {
  if (isValidReturnToUrl(returnTo, baseUrl)) {
    return returnTo
  }

  return getPendingInviteReturnTo(baseUrl) ?? undefined
}

export const buildSignInPathWithReturnTo = (returnTo: string | undefined, baseUrl: string): string => {
  const effectiveReturnTo = getEffectiveReturnTo(returnTo, baseUrl)

  if (effectiveReturnTo == null) {
    return APP_ROUTES.auth.signInPage
  }

  return `${APP_ROUTES.auth.signInPage}?returnTo=${encodeURIComponent(effectiveReturnTo)}`
}
